import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Sparkles, ArrowRight } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const STEPS = ["Living", "Activity", "Household", "Preferences", "Result"];

export default function AIMatchmaking() {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState({
        living_situation: "", activity_level: "", experience: "", household: "",
        other_pets: "", preferred_size: "", preferred_age: "", hours_alone: "", notes: ""
    });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const set = (k) => (v) => setForm({ ...form, [k]: v });

    async function submit() {
        setLoading(true);
        try {
            const { data } = await api.post("/matchmaking", form);
            setResult(data);
            setStep(4);
        } catch (e) {
            toast.error("Matchmaking failed. Please try again.");
        } finally { setLoading(false); }
    }

    return (
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10" data-testid="matchmaking-page">
            <div className="mb-10 text-center">
                <div className="inline-flex items-center gap-2 eyebrow text-[var(--rl-primary)] mb-3">
                    <Sparkles className="w-3.5 h-3.5" /> AI Matchmaking · powered by Claude
                </div>
                <h1 className="font-serif text-4xl lg:text-6xl tracking-tight">Find your dog.</h1>
                <p className="mt-3 text-[var(--rl-text-2)] max-w-xl mx-auto">Answer four short questions. We'll match your life to five dogs from our network.</p>
            </div>

            <div className="flex items-center justify-center gap-2 mb-10">
                {STEPS.map((s, i) => (
                    <div key={s} className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${i <= step ? "bg-[var(--rl-primary)] text-white" : "bg-[var(--rl-cream)] text-[var(--rl-text-3)]"}`}>{i + 1}</div>
                        <span className={`text-xs ${i === step ? "text-[var(--rl-text)] font-semibold" : "text-[var(--rl-text-3)]"} hidden sm:inline`}>{s}</span>
                        {i < STEPS.length - 1 && <div className={`w-8 h-px ${i < step ? "bg-[var(--rl-primary)]" : "bg-[var(--rl-border)]"}`} />}
                    </div>
                ))}
            </div>

            <div className="card-warm rounded-2xl p-8 lg:p-10">
                {step === 0 && (
                    <div data-testid="step-living">
                        <h2 className="font-serif text-3xl mb-6">Where will they live?</h2>
                        <div className="grid gap-3">
                            {["Apartment", "House with small yard", "House with large yard", "Farm / acreage"].map((o) => (
                                <ChoiceRow key={o} label={o} selected={form.living_situation === o} onClick={() => set("living_situation")(o)} />
                            ))}
                        </div>
                        <NextBtn disabled={!form.living_situation} onClick={() => setStep(1)} />
                    </div>
                )}

                {step === 1 && (
                    <div data-testid="step-activity">
                        <h2 className="font-serif text-3xl mb-6">Your activity level?</h2>
                        <div className="grid gap-3">
                            {["Low - short walks", "Moderate - daily walks and play", "High - runs, hikes, long adventures"].map((o) => (
                                <ChoiceRow key={o} label={o} selected={form.activity_level === o.split(" ")[0].toLowerCase()} onClick={() => set("activity_level")(o.split(" ")[0].toLowerCase())} />
                            ))}
                        </div>
                        <h3 className="font-serif text-xl mt-8 mb-4">Dog experience?</h3>
                        <div className="grid gap-3">
                            {["First-time owner", "Some experience", "Experienced with rescues"].map((o) => (
                                <ChoiceRow key={o} label={o} selected={form.experience === o} onClick={() => set("experience")(o)} />
                            ))}
                        </div>
                        <NextBtn disabled={!form.activity_level || !form.experience} onClick={() => setStep(2)} />
                    </div>
                )}

                {step === 2 && (
                    <div data-testid="step-household">
                        <h2 className="font-serif text-3xl mb-6">Your household?</h2>
                        <div className="grid gap-3">
                            {["Single", "Couple", "Family with kids (under 12)", "Family with teens", "Multi-generational"].map((o) => (
                                <ChoiceRow key={o} label={o} selected={form.household === o} onClick={() => set("household")(o)} />
                            ))}
                        </div>
                        <h3 className="font-serif text-xl mt-8 mb-4">Other pets?</h3>
                        <div className="grid gap-3">
                            {[{ v: "none", l: "None" }, { v: "dogs", l: "Other dog(s)" }, { v: "cats", l: "Cat(s)" }, { v: "both", l: "Dogs and cats" }].map((o) => (
                                <ChoiceRow key={o.v} label={o.l} selected={form.other_pets === o.v} onClick={() => set("other_pets")(o.v)} />
                            ))}
                        </div>
                        <NextBtn disabled={!form.household || !form.other_pets} onClick={() => setStep(3)} />
                    </div>
                )}

                {step === 3 && (
                    <div data-testid="step-preferences">
                        <h2 className="font-serif text-3xl mb-6">Your preferences?</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="eyebrow">Preferred size</label>
                                <Select value={form.preferred_size} onValueChange={set("preferred_size")}>
                                    <SelectTrigger className="mt-2 border-[var(--rl-border)]" data-testid="pref-size"><SelectValue placeholder="Any" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="small">Small</SelectItem>
                                        <SelectItem value="medium">Medium</SelectItem>
                                        <SelectItem value="large">Large</SelectItem>
                                        <SelectItem value="xl">Extra large</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="eyebrow">Preferred age</label>
                                <Select value={form.preferred_age} onValueChange={set("preferred_age")}>
                                    <SelectTrigger className="mt-2 border-[var(--rl-border)]" data-testid="pref-age"><SelectValue placeholder="Any" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="puppy">Puppy (under 1)</SelectItem>
                                        <SelectItem value="young">Young (1-3)</SelectItem>
                                        <SelectItem value="adult">Adult (4-7)</SelectItem>
                                        <SelectItem value="senior">Senior (8+)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="eyebrow">Hours alone per day</label>
                                <Select value={form.hours_alone} onValueChange={set("hours_alone")}>
                                    <SelectTrigger className="mt-2 border-[var(--rl-border)]" data-testid="pref-hours"><SelectValue placeholder="Select" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="0-2">0-2 hours</SelectItem>
                                        <SelectItem value="3-5">3-5 hours</SelectItem>
                                        <SelectItem value="6-8">6-8 hours</SelectItem>
                                        <SelectItem value="8+">8+ hours</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="mt-6">
                            <label className="eyebrow">Anything else? (optional)</label>
                            <Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} placeholder="Allergies, deal-breakers, dream dog…" className="mt-2 border-[var(--rl-border)]" data-testid="pref-notes" />
                        </div>
                        <Button className="btn-primary rounded-full h-12 px-8 mt-8" disabled={!form.hours_alone || loading} onClick={submit} data-testid="matchmaking-submit">
                            {loading ? "Matching…" : <>Find my matches <Sparkles className="w-4 h-4 ml-2" /></>}
                        </Button>
                    </div>
                )}

                {step === 4 && result && (
                    <div data-testid="matchmaking-results">
                        <h2 className="font-serif text-3xl mb-2">Your matches</h2>
                        <p className="text-[var(--rl-text-2)] mb-6">{result.summary}</p>
                        <div className="grid gap-5">
                            {result.matches.map((m, i) => (
                                <div key={m.dog.id} className="card-warm rounded-xl p-5 flex gap-5" data-testid={`match-${i}`}>
                                    <Link to={`/dogs/${m.dog.id}`} className="block w-32 h-32 rounded-lg overflow-hidden shrink-0 bg-[var(--rl-cream)]">
                                        <img src={m.dog.photo_url} alt={m.dog.name} className="w-full h-full object-cover" />
                                    </Link>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3">
                                            <div className="font-serif text-2xl">{m.dog.name}</div>
                                            <Badge className="bg-[var(--rl-primary)] text-white">{m.score}% match</Badge>
                                        </div>
                                        <div className="text-sm text-[var(--rl-text-2)]">{m.dog.breed} · {m.dog.age} · {m.dog.location}</div>
                                        <p className="mt-2 text-[var(--rl-text)] italic">"{m.reason}"</p>
                                        <Link to={`/dogs/${m.dog.id}`} className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[var(--rl-primary)]">
                                            Meet {m.dog.name} <ArrowRight className="w-4 h-4" />
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Button variant="outline" className="rounded-full mt-6" onClick={() => { setStep(0); setResult(null); }} data-testid="matchmaking-restart">Start over</Button>
                    </div>
                )}
            </div>
        </div>
    );
}

function ChoiceRow({ label, selected, onClick }) {
    return (
        <button onClick={onClick}
                className={`text-left px-5 py-3.5 rounded-lg border transition-colors ${selected ? "border-[var(--rl-primary)] bg-[#fff5f3] text-[var(--rl-text)]" : "border-[var(--rl-border)] hover:border-[var(--rl-border-strong)] bg-[var(--rl-surface)]"}`}
                data-testid={`choice-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            {label}
        </button>
    );
}

function NextBtn({ disabled, onClick }) {
    return (
        <div className="mt-8">
            <Button className="btn-primary rounded-full h-11 px-6" disabled={disabled} onClick={onClick} data-testid="next-step-btn">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
        </div>
    );
}
